/**
 * Swatch palette + helpers for the "Select a Color" grid picker.
 *
 * The grid is generated (not hand-listed) so it stays a single source of
 * truth: a greyscale top row (white → black) followed by a hue × shade matrix
 * — hues sweep left→right across the columns, lightness increases top→bottom.
 */

/** Number of columns in the picker grid. */
export const COLOR_PICKER_COLUMNS = 14;

const SHADE_ROWS = 8;

const hslToHex = (h: number, s: number, l: number): string => {
  const saturation = s / 100;
  const lightness = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number) =>
    lightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (value: number) =>
    Math.round(255 * value)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(channel(0))}${toHex(channel(8))}${toHex(channel(4))}`.toUpperCase();
};

const buildSwatches = (): string[] => {
  const swatches: string[] = [];

  // Greyscale row: white → black.
  for (let col = 0; col < COLOR_PICKER_COLUMNS; col += 1) {
    const lightness = Math.round(100 - (col / (COLOR_PICKER_COLUMNS - 1)) * 100);
    swatches.push(hslToHex(0, 0, lightness));
  }

  // Hue × shade matrix.
  for (let row = 0; row < SHADE_ROWS; row += 1) {
    const lightness = 22 + Math.round((row / (SHADE_ROWS - 1)) * 68);
    const saturation = 85 - Math.round((row / (SHADE_ROWS - 1)) * 25);
    for (let col = 0; col < COLOR_PICKER_COLUMNS; col += 1) {
      const hue = Math.round((col / COLOR_PICKER_COLUMNS) * 360);
      swatches.push(hslToHex(hue, saturation, lightness));
    }
  }

  return swatches;
};

export const COLOR_PICKER_SWATCHES: string[] = buildSwatches();

/** True when `value` is a usable `#RGB` / `#RRGGBB` / `#RRGGBBAA` hex string. */
export const isValidHex = (value: string): boolean =>
  /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim());
